import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useEffect, useState } from 'react';
import Loader from '../../_components/molecules/Loader';
import {
  Layers,
  OverviewCard,
  PageTitleAndSubTitle,
  SvgTable,
  ToggleGroup,
  WasteCalculatorCard,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import wasteCalculatorPageMessages from '../../core-utils/messages/wasteCalculatorPageMessages';
import { Typography } from '@HomaHealth/ui-component-library';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { useStores } from '../../stores';
import TabSelection from '../../_components/molecules/TabSelection';
import { pageScrollDown } from '../SummaryPage';
import CollapsibleTable from '../../_components/organisms/CollapsibleTable';
import {
  formatWastageCategoriesData,
  formatWasteCalculatorSummary,
  getTotalCostValue,
  getWasteCalculatorTableHeaders,
  getWasteCalculatorTableRows,
} from '../../core-utils/helper-utils/wasteCalculatorHelper';
import { getCohortValue } from '../../core-utils/helper-utils/overviewCardHelper';
import {
  numFormatterWithSuffix,
  numberToCurrencyFormatter,
} from '../../core-utils/helper-utils/helper';
import OverviewCardTooltipContent from '../../_components/atoms/OverviewCardTooltipContent';
import {
  CostValues,
  MemberValues,
} from '../../core-utils/helper-utils/summaryReportHelper';
import axios from 'axios';
import properties from '../../core-utils/properties';
import Snackbar from '../../_components/atoms/Snackbar';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import { WASTE_CALCULATOR_DETAILS } from '../../core-utils/routes';
import { useNavigate } from 'react-router-dom';
import { getRecommendationsData } from '../../core-utils/helper-utils/recommendationsTabHelper';
import RecommendationsTab from '../../_components/organisms/RecommendationsTab';
import ReportsModal from '../../_components/organisms/ReportsModal';
import useTotalEligible from '../../core-utils/custom-hooks/TotalEligible';
import DateFilter from '../../_components/molecules/DateFilter';
import { useAPIPayload } from '../../core-utils/helper-utils/apiPayloadHelper';
import usePlanDates from '../../core-utils/custom-hooks/PlanDates';

const WasteCalculatorPage = () => {
  const useStyles = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
      padding: '40px 0px 66px 63px',
    },
    titleAndSubtitleGrid: {
      width: '1182px',
      position: 'relative',
    },
    forAndEditDuration: {
      position: 'absolute',
      top: '-1px',
      left: '130px',
      display: 'flex',
      alignItems: 'center',
    },
    toggleGroup: {
      position: 'absolute',
      left: '875px',
      top: '14px',
    },
    icon: {
      height: '16px',
      width: '16px',
    },
  });

  //constants
  const { classes } = useStyles();
  const navigate = useNavigate();
  const pageMessages = wasteCalculatorPageMessages.WASTE_CALCULATOR_PAGE;
  const employerGroupStore = useStores().employerGroupStore;
  const recommendationsStore = useStores().recommendedTabStore;
  const wasteCalculatorStore = useStores().wasteCalculatorStore;

  //states
  const [pageLoading, setPageLoading] = useState<boolean>(false);
  const [loader] = useState<any>(<Loader />);
  const [toggleState, setToggleState] = useState<string>('left');
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [cohortMembers, setCohortMembers] = useState<MemberValues>({
    members: '0',
    employees: '0',
    dependents: '0',
  });
  const [cohortCost, setCohortCost] = useState<CostValues>({
    rx: '$0.00',
    medical: '$0.00',
    total: '$0.00',
  });
  const [totalClaims, setTotalClaims] = useState<number>(0);
  const [wastageCategoriesData, setWastageCategoriesData] = useState<any>();

  const [openReportsModal, setOpenReportsModal] = useState(false);
  //snacker bar variables and functions
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });
  const handleCloseSnackBar = () => {
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };

  const [recommendationLoading, setRecommendationLoading] = useState<boolean>(
    false,
  );
  const [recommendationFetched, setRecommendationFetched] = useState<boolean>(
    false,
  );
  const handleSelectedIndex = (index: number) => {
    setSelectedTabIndex(index);
    if (!recommendationFetched) {
      getRecommendationsData(
        employerGroupStore?.employerGroupResponseData?.eg_nid,
        pageMessages.RECOMMENDATIONS_TAB.TITLE,
        recommendationsStore,
        setRecommendationLoading,
      );
      setRecommendationFetched(true);
    }
  };

  //edit duration variables and functions
  const quaterlyReportStore = useStores().quaterlyReportStore;
  const {
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    isIncurredDate,
    isPaidDate,
    isPlanDatesFetched,
    aggregationParameter,
  } = quaterlyReportStore;

  const { getPlanDates, loading: isPlanDatesLoading } = usePlanDates();
  const payload = useAPIPayload();

  const {
    overAllEnrolled,
    overallTotalCost,
    getTotalEligible,
  } = useTotalEligible(
    aggregationParameter === 'paid' ? paidStartDate : incurredStartDate,
    aggregationParameter === 'paid' ? paidEndDate : incurredEndDate,
    employerGroupStore,
  );

  //api calls
  const getWasteCalculatorSummary = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.wasteCalculatorBaseUrl +
          properties.wastageSummary,

        {
          ...payload,
          member_unique_id_list: [],
          claim_id_list: [],
        },
      )
      .then((response) => {
        const formattedData = formatWasteCalculatorSummary(
          response?.data?.data,
        );
        setCohortCost(formattedData.costValues);
        setCohortMembers(formattedData.memberValues);
        setTotalClaims(formattedData.totalClaims);
      })
      .catch((error) => {
        handleOpenSnackBar(error.response?.data?.error, 'error', 8000);
      });
  };

  const getWasteCalculatorCategoriesDistribution = async () => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.wasteCalculatorBaseUrl +
          properties.wastageCategories,
        {
          ...payload,
          member_unique_id_list: [],
          claim_id_list: [],
        },
      )
      .then((response) => {
        wasteCalculatorStore?.removeAllData();
        const formattedData = formatWastageCategoriesData(response?.data?.data);
        wasteCalculatorStore?.setCategoryMembersMap(
          formattedData?.localCategoryMembersMap,
        );
        setWastageCategoriesData(formattedData?.updatedWastageCategoriesData);
      })
      .catch((error) => {
        handleOpenSnackBar(error.response?.data?.error, 'error', 8000);
      });
  };

  //TO-DO recommendations logic part
  recommendationsStore.setTotalRecommendedCategories(0);

  const WasteCalculatorCards = () => {
    const useStyles = makeStyles()({
      root: {
        display: 'flex',
        flexDirection: 'column',
      },
      cardsGrid: {
        display: 'flex',
        flexDirection: 'column',
      },
    });
    const { classes } = useStyles();

    const GroupOfCards = (title: string, cardsData: any) => {
      return (
        <Grid style={{ marginBottom: '38px' }}>
          <Grid>
            <Typography
              fontSize="18px"
              lineHeight="26px"
              textColor={patientAccessTheme.palette.text.highemphasis}
              fontType="medium"
              margin="0px 0px 24px 0px"
            >
              {title}
            </Typography>
          </Grid>
          <Grid style={{ display: 'flex' }}>
            {cardsData?.map((eachCateg: any, index: number) => {
              return (
                <div
                  key={index}
                  style={{
                    marginRight:
                      index !== cardsData?.length - 1 ? '24px' : '0px',
                  }}
                >
                  <WasteCalculatorCard
                    title={eachCateg?.category}
                    subTitle={pageMessages.CARD.SUB_TITLE}
                    labels={pageMessages.CARD.LABELS}
                    values={[
                      numberToCurrencyFormatter(
                        eachCateg?.members ?? 0,
                        false,
                        0,
                      ),
                      getTotalCostValue(eachCateg?.total_cost),
                      numberToCurrencyFormatter(
                        eachCateg?.claims_count ?? 0,
                        false,
                        0,
                      ),
                    ]}
                    image={eachCateg.image}
                    handleOnClick={() => {
                      wasteCalculatorStore.setCategory(eachCateg?.category);
                      navigate(WASTE_CALCULATOR_DETAILS, {
                        state: {
                          selectedFactor: eachCateg?.category,
                          claimIds: eachCateg?.claims_ids,
                        },
                      });
                    }}
                  />
                </div>
              );
            })}
          </Grid>
        </Grid>
      );
    };
    return (
      <Grid container className={classes.root}>
        <Grid style={{ display: 'flex', marginBottom: '34px' }}>
          <Grid style={{ marginRight: '24px' }}>
            <OverviewCardTooltipContent
              type={'member'}
              cohortMemberValues={cohortMembers}
              totalMemberValues={{
                dependents: overAllEnrolled.dependents,
                employees: overAllEnrolled.employees,
                members: overAllEnrolled.totalCount,
              }}
              overviewCard={
                <OverviewCard
                  title={pageMessages.COHORT_MEMBERS}
                  value={getCohortValue(
                    cohortMembers?.members,
                    overAllEnrolled.totalCount,
                  )}
                  rootStyles={{
                    width: '176px',
                    fontWeight: 600,
                    fontSize: ' 24px',
                  }}
                />
              }
            />
          </Grid>
          <Grid style={{ marginRight: '24px' }}>
            <OverviewCardTooltipContent
              type={'cost'}
              cohortCostValues={cohortCost}
              totalCostValues={{
                medical: overallTotalCost.totalMedical,
                rx: overallTotalCost.totalRx,
                total: overallTotalCost.totalCost,
              }}
              overviewCard={
                <OverviewCard
                  title={pageMessages.COHORT_COST}
                  value={getCohortValue(
                    '$' +
                      numFormatterWithSuffix(
                        Number(
                          cohortCost.total?.substring(1)?.replaceAll(',', ''),
                        ),
                      ),
                    overallTotalCost.totalCost,
                  )}
                  rootStyles={{
                    width: '216px',
                  }}
                  labelsMinWidth="72px"
                />
              }
            />
          </Grid>
          <Grid style={{ marginRight: '24px' }}>
            <OverviewCard
              title={pageMessages.COHORT_CLAIMS}
              value={numberToCurrencyFormatter(totalClaims, false, 0)}
              rootStyles={{
                width: '176px',
                fontWeight: 600,
                fontSize: ' 24px',
              }}
            />
          </Grid>
        </Grid>
        <Grid>
          {toggleState === 'left' ? (
            <div>
              {wastageCategoriesData?.map((item: any, index: number) => (
                <React.Fragment key={index}>
                  {GroupOfCards(item.mainCategory, item.wastageCategories)}
                </React.Fragment>
              ))}
            </div>
          ) : (
            <div>
              <CollapsibleTable
                tableData={wastageCategoriesData}
                tableHeaders={getWasteCalculatorTableHeaders()}
                tableRows={getWasteCalculatorTableRows}
              />
            </div>
          )}
        </Grid>
      </Grid>
    );
  };

  const getAllApis = async () => {
    setPageLoading(true);
    isPlanDatesFetched &&
      (await Promise.all([
        getWasteCalculatorCategoriesDistribution(),
        getWasteCalculatorSummary(),
        getTotalEligible(),
      ])
        .then(() => {
          setPageLoading(false);
        })
        .finally(() => {
          setPageLoading(false);
        }));

    setPageLoading(false);
  };

  //useEffects
  useEffect(() => {
    getAllApis();
  }, [
    isPlanDatesFetched,
    isIncurredDate,
    isPaidDate,
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    aggregationParameter,
  ]);

  useEffect(() => {
    const fetchPlanDates = async () => {
      if (!isPlanDatesFetched) {
        await getPlanDates();
      }
    };
    if (!employerGroupStore?.employerGroupResponseData?.eg_nid[0]) {
      return;
    }
    fetchPlanDates();
  }, [employerGroupStore?.employerGroupResponseData?.eg_nid]);

  return (
    <>
      {pageLoading || isPlanDatesLoading ? (
        loader
      ) : (
        <Grid container className={classes.root}>
          <Grid className={classes.titleAndSubtitleGrid}>
            <PageTitleAndSubTitle
              title={pageMessages.TITLE}
              subTitle={pageMessages.SUB_TITLE}
              showDownloadButton={true}
              downloadButtonAction={() => setOpenReportsModal(true)}
            />
            <div className={classes.forAndEditDuration}>
              <Typography
                fontSize="17px"
                lineHeight="21px"
                textColor={patientAccessTheme.palette.text.mediumemphasis}
                fontType="semiBold"
                margin="0px 8px"
              >
                {pageMessages.FOR}
              </Typography>
              <DateFilter />
            </div>
            <div className={classes.toggleGroup}>
              <ToggleGroup
                leftValue="Card View"
                rightValue="Table View"
                leftIcon={<Layers className={classes.icon} />}
                rightIcon={<SvgTable className={classes.icon} />}
                setSelectedToggle={function (
                  value: React.SetStateAction<'left' | 'right'>,
                ): void {
                  value === 'left'
                    ? setToggleState('left')
                    : setToggleState('right');
                }}
              />
            </div>
            <TabSelection
              selectedTabIndex={selectedTabIndex}
              setSelectedTabIndex={handleSelectedIndex}
              recommendationFetched={recommendationFetched}
            />
          </Grid>
          <Grid style={{ marginTop: '27px' }}>
            {selectedTabIndex === 0 && <WasteCalculatorCards />}
            {selectedTabIndex === 1 && (
              <RecommendationsTab
                recommendationLoading={recommendationLoading}
              />
            )}
          </Grid>
          <div id="scroll-top-element"></div>
          {pageScrollDown('scroll-top-element')}
        </Grid>
      )}
      <Snackbar
        {...snackbarState}
        handleClose={handleCloseSnackBar}
        vertical="bottom"
        horizontal="right"
      />
      <ReportsModal
        open={openReportsModal}
        setOpen={setOpenReportsModal}
        startDate={
          aggregationParameter === 'paid' ? paidStartDate : incurredStartDate
        }
        endDate={
          aggregationParameter === 'paid' ? paidEndDate : incurredEndDate
        }
      />
    </>
  );
};

export default WasteCalculatorPage;
